import {
  Add01Icon,
  ArrowRight01Icon,
  BookOpen01Icon,
  Calendar03Icon,
  CheckmarkCircle02Icon,
  MagicWand01Icon,
  School01Icon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useMemo, useState } from 'react';
import { addDays, todayInTimezone } from '../domain/date';
import { subjectsFor, type Curriculum, type ExamTrack, type GradeLevel } from '../domain/catalog';
import type { OnboardingInput, RoadmapSubject, SubjectSetup } from '../domain/platform';
import { AccentPicker } from './AccentPicker';
import { completeOnboarding, generateOnboardingSyllabus } from './api';
import { MeshBackdrop } from './qol';
import { applyAccent, DEFAULT_ACCENT } from './theme';

const CURRICULA: Curriculum[] = ['CBSE', 'ICSE', 'ISC', 'STATE', 'IB', 'OTHER'];
const EXAM_TRACKS: { value: ExamTrack; label: string }[] = [
  { value: 'SCHOOL', label: 'School' },
  { value: 'BOARDS', label: 'Boards' },
  { value: 'JEE', label: 'JEE' },
  { value: 'NEET', label: 'NEET' },
  { value: 'CUET', label: 'CUET' },
  { value: 'OLYMPIAD', label: 'Olympiad' },
  { value: 'OTHER', label: 'Other' },
];
const DAYS = [
  { value: 1, label: 'M' },
  { value: 2, label: 'T' },
  { value: 3, label: 'W' },
  { value: 4, label: 'T' },
  { value: 5, label: 'F' },
  { value: 6, label: 'S' },
  { value: 0, label: 'S' },
];

interface OnboardingDraft {
  step: number;
  username: string;
  grade: GradeLevel;
  curriculum: Curriculum;
  examTrack: ExamTrack;
  subjects: SubjectSetup[];
  roadmaps: RoadmapSubject[];
  startDate: string;
  targetDate: string;
  studyDays: number[];
  dailyMinutes: number;
  autoRebalance: boolean;
  accentColor: string;
}

function readDraft(key: string): Partial<OnboardingDraft> | null {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return null;
    const draft = JSON.parse(value) as Partial<OnboardingDraft>;
    const validSubjects =
      draft.subjects === undefined ||
      (Array.isArray(draft.subjects) &&
        draft.subjects.every(
          (subject) =>
            typeof subject?.key === 'string' &&
            typeof subject.title === 'string' &&
            Array.isArray(subject.topics) &&
            subject.topics.every((topic) => typeof topic === 'string'),
        ));
    const validRoadmaps =
      draft.roadmaps === undefined ||
      (Array.isArray(draft.roadmaps) &&
        draft.roadmaps.every(
          (roadmap) =>
            typeof roadmap?.key === 'string' &&
            typeof roadmap.title === 'string' &&
            Array.isArray(roadmap.chapters) &&
            roadmap.chapters.every(
              (chapter) =>
                typeof chapter?.title === 'string' &&
                Array.isArray(chapter.subtopics) &&
                chapter.subtopics.every((topic) => typeof topic === 'string'),
            ),
        ));
    if (!validSubjects || !validRoadmaps || (draft.studyDays && !Array.isArray(draft.studyDays))) {
      window.localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
}

export function Onboarding({ userId, initialName }: { userId: string; initialName: string }) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const today = todayInTimezone(timezone);
  const draftKey = `tarpy-onboarding-v1:${userId}`;
  const [draft] = useState(() => readDraft(draftKey));
  const [step, setStep] = useState(() => Math.max(0, Math.min(3, draft?.step ?? 0)));
  const [username, setUsername] = useState(draft?.username || initialName);
  const [grade, setGrade] = useState<GradeLevel>(draft?.grade ?? 12);
  const [curriculum, setCurriculum] = useState<Curriculum>(draft?.curriculum ?? 'CBSE');
  const [examTrack, setExamTrack] = useState<ExamTrack>(draft?.examTrack ?? 'BOARDS');
  const [subjects, setSubjects] = useState<SubjectSetup[]>(draft?.subjects ?? []);
  const [subjectQuery, setSubjectQuery] = useState('');
  const [roadmaps, setRoadmaps] = useState<RoadmapSubject[]>(draft?.roadmaps ?? []);
  const [customSubject, setCustomSubject] = useState('');
  const [startDate, setStartDate] = useState(draft?.startDate ?? today);
  const [targetDate, setTargetDate] = useState(draft?.targetDate ?? addDays(today, 90));
  const [studyDays, setStudyDays] = useState(draft?.studyDays ?? [1, 2, 3, 4, 5, 6]);
  const [dailyMinutes, setDailyMinutes] = useState(draft?.dailyMinutes ?? 180);
  const [autoRebalance, setAutoRebalance] = useState(draft?.autoRebalance ?? true);
  const [accentColor, setAccentColor] = useState(draft?.accentColor ?? DEFAULT_ACCENT);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationSeconds, setGenerationSeconds] = useState(0);
  const [generationModel, setGenerationModel] = useState<string | null>(null);
  const [generationSource, setGenerationSource] = useState<'cache' | 'mixed' | 'generated' | null>(
    null,
  );
  const [incorrectSubject, setIncorrectSubject] = useState<string | null>(null);
  const [correction, setCorrection] = useState('');
  const [manualTopics, setManualTopics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = useMemo(() => subjectsFor(grade, examTrack), [examTrack, grade]);
  const visibleOptions = useMemo(() => {
    const query = subjectQuery.trim().toLowerCase();
    return query
      ? options.filter((subject) => subject.label.toLowerCase().includes(query))
      : options;
  }, [options, subjectQuery]);

  useEffect(() => {
    if (!generating) return;
    const started = Date.now();
    const timer = window.setInterval(
      () => setGenerationSeconds(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [generating]);

  useEffect(() => {
    applyAccent(accentColor);
  }, [accentColor]);

  useEffect(() => {
    const nextDraft: OnboardingDraft = {
      step,
      username,
      grade,
      curriculum,
      examTrack,
      subjects,
      roadmaps,
      startDate,
      targetDate,
      studyDays,
      dailyMinutes,
      autoRebalance,
      accentColor,
    };
    try {
      window.localStorage.setItem(draftKey, JSON.stringify(nextDraft));
    } catch {
      // Private browsing or storage pressure should not block onboarding.
    }
  }, [
    autoRebalance,
    accentColor,
    curriculum,
    dailyMinutes,
    draftKey,
    examTrack,
    grade,
    roadmaps,
    startDate,
    step,
    studyDays,
    subjects,
    targetDate,
    username,
  ]);

  function toggleSubject(key: string, title: string) {
    setSubjects((current) =>
      current.some((subject) => subject.key === key)
        ? current.filter((subject) => subject.key !== key)
        : [...current, { key, title, topics: [] }],
    );
  }

  function addCustomSubject() {
    const title = customSubject.trim();
    if (!title) return;
    const base =
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'custom';
    const key = subjects.some((subject) => subject.key === base)
      ? `${base}-${subjects.length + 1}`
      : base;
    setSubjects((current) => [...current, { key, title, topics: [] }]);
    setCustomSubject('');
  }

  function setTopics(key: string, value: string) {
    const topics = value
      .split(/\n|,/)
      .map((topic) => topic.trim())
      .filter(Boolean);
    setSubjects((current) =>
      current.map((subject) => (subject.key === key ? { ...subject, topics } : subject)),
    );
  }

  async function finish() {
    const input: OnboardingInput = {
      username: username.trim(),
      grade,
      curriculum,
      examTrack,
      subjects,
      startDate,
      targetDate,
      timezone,
      studyDays,
      dailyMinutes,
      autoRebalance,
      accentColor,
    };
    setSaving(true);
    setError(null);
    try {
      await completeOnboarding(input);
      window.localStorage.removeItem(draftKey);
      window.location.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create your plans.');
      setSaving(false);
    }
  }

  function roadmapTopic(chapter: string, subtopic: string) {
    return `${chapter} — ${subtopic}`;
  }

  function allRoadmapTopics(roadmap: RoadmapSubject, fromChapter = 0) {
    return roadmap.chapters.flatMap((chapter, index) =>
      index < fromChapter
        ? []
        : chapter.subtopics.map((subtopic) => roadmapTopic(chapter.title, subtopic)),
    );
  }

  async function generateTopics(subjectKey?: string, feedback?: string) {
    setGenerating(true);
    setGenerationSeconds(0);
    setGenerationModel(null);
    setError(null);
    try {
      const result = await generateOnboardingSyllabus({
        grade,
        curriculum,
        examTrack,
        subjects: subjects.map(({ key, title }) => ({ key, title })),
        ...(subjectKey
          ? {
              forceRegenerate: true,
              incorrectSubjectKeys: [subjectKey],
              feedback: feedback ?? '',
            }
          : {}),
      });
      setRoadmaps(result.subjects);
      setSubjects((current) =>
        current.map((subject) => {
          const roadmap = result.subjects.find((candidate) => candidate.key === subject.key);
          if (!roadmap) return subject;
          if (subjectKey && subject.key !== subjectKey && subject.topics.length) return subject;
          return { ...subject, topics: allRoadmapTopics(roadmap) };
        }),
      );
      setGenerationModel(result.model);
      setGenerationSource(result.source);
      setManualTopics(false);
      setIncorrectSubject(null);
      setCorrection('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not generate your syllabus.');
    } finally {
      setGenerating(false);
    }
  }

  function continueFromStep() {
    if (step === 1) {
      setStep(2);
      if (subjects.some((subject) => subject.topics.length === 0)) void generateTopics();
      return;
    }
    if (step === 3) void finish();
    else setStep((current) => current + 1);
  }

  const canContinue =
    (step === 0 && username.trim().length >= 2) ||
    (step === 1 && subjects.length > 0) ||
    (step === 2 && !generating && subjects.every((subject) => subject.topics.length > 0)) ||
    (step === 3 && studyDays.length > 0 && targetDate >= startDate);

  return (
    <main className="onboarding-shell">
      <MeshBackdrop />
      <header className="onboarding-brand">
        <img src="/brand/icon.svg" alt="" />
        <b>tarpy.party</b>
        <span>{step + 1}/4</span>
      </header>
      <section className="onboarding-card">
        {step === 0 && (
          <>
            <StepTitle icon={UserIcon} eyebrow="You" title="Set your direction" />
            <div className="onboarding-grid two">
              <label>
                Username
                <input
                  value={username}
                  maxLength={40}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </label>
              <label>
                Class
                <select
                  value={grade}
                  onChange={(event) => setGrade(Number(event.target.value) as GradeLevel)}
                >
                  {[9, 10, 11, 12].map((value) => (
                    <option value={value} key={value}>
                      Class {value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Board
                <select
                  value={curriculum}
                  onChange={(event) => setCurriculum(event.target.value as Curriculum)}
                >
                  {CURRICULA.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Goal
                <select
                  value={examTrack}
                  onChange={(event) => setExamTrack(event.target.value as ExamTrack)}
                >
                  {EXAM_TRACKS.map((track) => (
                    <option value={track.value} key={track.value}>
                      {track.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <AccentPicker value={accentColor} onChange={setAccentColor} />
          </>
        )}

        {step === 1 && (
          <>
            <StepTitle icon={BookOpen01Icon} eyebrow={`Class ${grade}`} title="Choose subjects" />
            <input
              className="subject-search"
              value={subjectQuery}
              aria-label="Find a subject"
              placeholder="Find a subject"
              onChange={(event) => setSubjectQuery(event.target.value)}
            />
            <div className="subject-picker">
              {visibleOptions.map((subject) => {
                const selected = subjects.some((value) => value.key === subject.key);
                return (
                  <button
                    className={selected ? 'selected' : ''}
                    type="button"
                    key={subject.key}
                    onClick={() => toggleSubject(subject.key, subject.label)}
                  >
                    {selected && <HugeiconsIcon icon={CheckmarkCircle02Icon} size={15} />}
                    {subject.label}
                  </button>
                );
              })}
              {!visibleOptions.length && <p>No matching subject. Add it below.</p>}
            </div>
            <div className="custom-subject">
              <input
                value={customSubject}
                placeholder="Add another subject"
                onChange={(event) => setCustomSubject(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && addCustomSubject()}
              />
              <button
                className="icon-button"
                type="button"
                onClick={addCustomSubject}
                aria-label="Add subject"
              >
                <HugeiconsIcon icon={Add01Icon} size={17} />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <StepTitle
              icon={School01Icon}
              eyebrow="Auto syllabus"
              title={generating ? 'Building your topic map' : 'Review your topics'}
            />
            {generating ? (
              <div className="syllabus-generating" aria-live="polite">
                <span className="generating-orbit">
                  <HugeiconsIcon icon={MagicWand01Icon} size={22} />
                </span>
                <strong>
                  {generationSeconds < 20
                    ? 'Reading syllabus sources'
                    : generationSeconds < 60
                      ? 'Ordering chapters for your goal'
                      : 'GLM is still working — keeping the request open'}
                </strong>
                <p>
                  {curriculum} · Class {grade} · {examTrack}
                </p>
                <small>{generationSeconds}s · this can take up to 3 minutes</small>
                <div className="generation-subjects">
                  {subjects.map((subject) => (
                    <span key={subject.key}>{subject.title}</span>
                  ))}
                </div>
              </div>
            ) : roadmaps.length > 0 ? (
              <>
                <div className="generated-syllabus-meta">
                  <span>
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} size={15} /> Generated from your
                    class, board, and goal
                  </span>
                  <small>{generationSource === 'cache' ? 'Roadmap index' : 'AI researched'}</small>
                </div>
                <div className="roadmap-generation-actions">
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => void generateTopics()}
                  >
                    <HugeiconsIcon icon={MagicWand01Icon} size={15} /> Regenerate automatically
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => setManualTopics((current) => !current)}
                  >
                    {manualTopics ? 'Hide manual editor' : 'Edit topics manually'}
                  </button>
                </div>
                <div className="roadmap-editor">
                  {roadmaps.map((roadmap) => {
                    const setup = subjects.find((subject) => subject.key === roadmap.key);
                    const selected = new Set(setup?.topics ?? []);
                    const allTopics = allRoadmapTopics(roadmap);
                    return (
                      <section className="roadmap-subject" key={roadmap.key}>
                        <header>
                          <div>
                            <h3>{roadmap.title}</h3>
                            <p>
                              {selected.size} of {allTopics.length} subtopics selected
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setIncorrectSubject(roadmap.key);
                              setCorrection('');
                            }}
                          >
                            Roadmap incorrect?
                          </button>
                        </header>
                        <label className="roadmap-start">
                          Start from
                          <select
                            onChange={(event) => {
                              const topics = allRoadmapTopics(roadmap, Number(event.target.value));
                              setSubjects((current) =>
                                current.map((subject) =>
                                  subject.key === roadmap.key ? { ...subject, topics } : subject,
                                ),
                              );
                            }}
                            defaultValue="0"
                          >
                            {roadmap.chapters.map((chapter, index) => (
                              <option key={chapter.title} value={index}>
                                {chapter.title}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="roadmap-actions">
                          <button
                            type="button"
                            onClick={() =>
                              setSubjects((current) =>
                                current.map((subject) =>
                                  subject.key === roadmap.key
                                    ? { ...subject, topics: allTopics }
                                    : subject,
                                ),
                              )
                            }
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setSubjects((current) =>
                                current.map((subject) =>
                                  subject.key === roadmap.key
                                    ? { ...subject, topics: [] }
                                    : subject,
                                ),
                              )
                            }
                          >
                            Clear
                          </button>
                        </div>
                        <div className="roadmap-chapters">
                          {roadmap.chapters.map((chapter) => {
                            const chapterTopics = chapter.subtopics.map((subtopic) =>
                              roadmapTopic(chapter.title, subtopic),
                            );
                            const selectedCount = chapterTopics.filter((topic) =>
                              selected.has(topic),
                            ).length;
                            return (
                              <details key={chapter.title} open={roadmap.chapters.length <= 5}>
                                <summary>
                                  <label onClick={(event) => event.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={selectedCount === chapterTopics.length}
                                      ref={(input) => {
                                        if (input)
                                          input.indeterminate =
                                            selectedCount > 0 &&
                                            selectedCount < chapterTopics.length;
                                      }}
                                      onChange={(event) =>
                                        setSubjects((current) =>
                                          current.map((subject) => {
                                            if (subject.key !== roadmap.key) return subject;
                                            const next = new Set(subject.topics);
                                            for (const topic of chapterTopics)
                                              event.target.checked
                                                ? next.add(topic)
                                                : next.delete(topic);
                                            return { ...subject, topics: [...next] };
                                          }),
                                        )
                                      }
                                    />
                                    {chapter.title}
                                  </label>
                                  <small>
                                    {selectedCount}/{chapterTopics.length}
                                  </small>
                                </summary>
                                <div>
                                  {chapter.subtopics.map((subtopic) => {
                                    const topic = roadmapTopic(chapter.title, subtopic);
                                    return (
                                      <label key={subtopic}>
                                        <input
                                          type="checkbox"
                                          checked={selected.has(topic)}
                                          onChange={(event) =>
                                            setSubjects((current) =>
                                              current.map((subject) => {
                                                if (subject.key !== roadmap.key) return subject;
                                                const next = new Set(subject.topics);
                                                event.target.checked
                                                  ? next.add(topic)
                                                  : next.delete(topic);
                                                return { ...subject, topics: [...next] };
                                              }),
                                            )
                                          }
                                        />
                                        {subtopic}
                                      </label>
                                    );
                                  })}
                                </div>
                              </details>
                            );
                          })}
                        </div>
                        {incorrectSubject === roadmap.key && (
                          <div className="roadmap-correction">
                            <label>
                              What is incorrect?
                              <textarea
                                autoFocus
                                value={correction}
                                maxLength={1000}
                                onChange={(event) => setCorrection(event.target.value)}
                                placeholder="Missing chapters, wrong board scope, incorrect order…"
                              />
                            </label>
                            <div>
                              <button
                                className="text-button"
                                type="button"
                                onClick={() => setIncorrectSubject(null)}
                              >
                                Cancel
                              </button>
                              <button
                                className="primary-button compact-button"
                                type="button"
                                disabled={correction.trim().length < 5}
                                onClick={() => void generateTopics(roadmap.key, correction.trim())}
                              >
                                Regenerate this subject
                              </button>
                            </div>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
                {generationModel && (
                  <p className="generation-model">
                    {generationSource === 'cache'
                      ? 'Loaded from the shared roadmap index'
                      : `Generated with ${generationModel} + web syllabus research`}
                  </p>
                )}
              </>
            ) : (
              <div className="syllabus-fallback">
                <strong>Generate your roadmap automatically</strong>
                <p>Tarpy will use your class, board, goal, and selected subjects.</p>
                <div>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => void generateTopics()}
                  >
                    <HugeiconsIcon icon={MagicWand01Icon} size={15} /> Generate roadmap
                    automatically
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => setManualTopics(true)}
                  >
                    Enter manually
                  </button>
                </div>
              </div>
            )}
            {manualTopics && (
              <div className="topic-setup-list manual-topic-list">
                {subjects.map((subject) => (
                  <label key={subject.key}>
                    <span>
                      {subject.title}
                      <small>{subject.topics.length} topics</small>
                    </span>
                    <textarea
                      value={subject.topics.join('\n')}
                      placeholder="One chapter or topic per line"
                      onChange={(event) => setTopics(subject.key, event.target.value)}
                    />
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <StepTitle icon={Calendar03Icon} eyebrow="Calendar" title="Set your pace" />
            <div className="onboarding-grid two">
              <label>
                Start
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </label>
              <label>
                Target
                <input
                  type="date"
                  value={targetDate}
                  min={startDate}
                  onChange={(event) => setTargetDate(event.target.value)}
                />
              </label>
              <label>
                Minutes each day
                <input
                  type="number"
                  min="20"
                  max="720"
                  step="10"
                  value={dailyMinutes}
                  onChange={(event) => setDailyMinutes(Number(event.target.value))}
                />
              </label>
              <fieldset className="day-picker">
                <legend>Study days</legend>
                <div>
                  {DAYS.map((day) => (
                    <button
                      className={studyDays.includes(day.value) ? 'selected' : ''}
                      type="button"
                      key={day.value}
                      onClick={() =>
                        setStudyDays((current) =>
                          current.includes(day.value)
                            ? current.filter((value) => value !== day.value)
                            : [...current, day.value],
                        )
                      }
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>
            <label className="auto-rebalance-option">
              <input
                type="checkbox"
                checked={autoRebalance}
                onChange={(event) => setAutoRebalance(event.target.checked)}
              />
              <span>
                <b>Auto-rebalance</b>
                <small>Redistribute missed work across the remaining month.</small>
              </span>
            </label>
          </>
        )}

        {error && <p className="onboarding-error">{error}</p>}
        <footer className="onboarding-actions">
          {step > 0 && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => setStep((current) => current - 1)}
            >
              Back
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            disabled={!canContinue || saving}
            onClick={continueFromStep}
          >
            {saving ? 'Building…' : step === 3 ? 'Build my plans' : 'Continue'}
            <HugeiconsIcon icon={step === 3 ? MagicWand01Icon : ArrowRight01Icon} size={16} />
          </button>
        </footer>
      </section>
    </main>
  );
}

function StepTitle({
  icon,
  eyebrow,
  title,
}: {
  icon: typeof UserIcon;
  eyebrow: string;
  title: string;
}) {
  return (
    <header className="onboarding-title">
      <span>
        <HugeiconsIcon icon={icon} size={19} />
      </span>
      <div>
        <small>{eyebrow}</small>
        <h1>{title}</h1>
      </div>
    </header>
  );
}
